import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * Hook for prefetching data on user interaction (hover, focus, etc.)
 * Reduces perceived loading time by loading data before navigation
 */
export const usePrefetch = () => {
  const queryClient = useQueryClient();
  
  const prefetchQuery = useCallback((
    queryKey: string[], 
    queryFn: () => Promise<any>,
    options?: {
      staleTime?: number;
      delay?: number;
    }
  ) => {
    const { staleTime = 10 * 1000, delay = 200 } = options || {};
    
    // Debounce prefetch to avoid excessive requests on quick hover
    const timeoutId = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey,
        queryFn,
        staleTime,
      });
    }, delay);
    
    // Return cleanup function to cancel prefetch if user moves away
    return () => clearTimeout(timeoutId);
  }, [queryClient]);
  
  /**
   * Higher-order function to wrap navigation links with prefetch
   * Usage: <Link {...getPrefetchProps('dashboard', fetchDashboard)}>
   */
  const getPrefetchProps = useCallback((
    queryKey: string | string[],
    queryFn: () => Promise<any>,
    options?: { staleTime?: number; delay?: number }
  ) => {
    const key = Array.isArray(queryKey) ? queryKey : [queryKey];
    
    return {
      onMouseEnter: () => prefetchQuery(key, queryFn, options),
      onFocus: () => prefetchQuery(key, queryFn, options),
    };
  }, [prefetchQuery]);
  
  return { 
    prefetchQuery,
    getPrefetchProps 
  };
};
